import type { Logger } from "@expert/logging";
import type { Draft } from "immer";
import type { WritableKeysOf } from "type-fest";
import type { StorageValue } from "zustand/middleware";

import { getLogger } from "@expert/logging";
import { configure } from "safe-stable-stringify";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { Task } from "../agent/task";
import type { TaskStatus, TaskStatusReason, WrappingState } from "../agent/types";
import type { ConferenceDetails, ConferenceParticipant, VoiceTask } from "../agent/voice";
import type { Session, SessionChangeReason } from "./types";

import { isTrainingTask, type TrainingTask } from "../agent/trainingTask";
import { isVoiceTask } from "../agent/voice";
import { SessionInstance } from "./session";

const safeStringify = configure({
    deterministic: false,
    maximumDepth: 10,
    strict: false,
});
const logger = getLogger({
    module: "SessionStore",
});

interface SessionStoreState {
    initialized: boolean;
    selectedSessionId: string;
    idleSession: SessionInstance;
    sessions: SessionInstance[];
}

export interface SessionStore extends SessionStoreState {
    /** Transactionally mutate a store with the immer WriteableDraft running the callers logic */
    mutateStore: (mutation: (state: Draft<SessionStore>) => void) => void;

    /** Ends the provided session and inserts the new session */
    addSession: (session: SessionInstance, shouldSelect?: boolean) => void;
    endSession: (sessionId: string, reason: SessionChangeReason) => void;
    setSelectedSession: (sessionId: string) => void;
    clearSelectedSession: () => void;
    addTaskToSession: (sessionId: string, task: Task) => void;
    updateTaskStatus: (taskId: string, status: TaskStatus, reason?: TaskStatusReason) => void;
    muteTask: (taskId: string, shouldMute: boolean) => void;
    setHold: (taskId: string, onHold: boolean) => void;
    setStartTimestamp: (taskId: string, timestamp: number) => void;
    setWrappingState: (wrappingState: WrappingState, sessionId: string) => void;
    clearWrappingState: (sessionId: string) => void;
    setConferenceStarted: (taskId: string, isStarted: boolean) => void;
    setCustomerLeftTheCall: (taskId: string) => void;
    upsertConferenceParticipant: (taskId: string, participant: ConferenceParticipant) => void;
    updateConferenceParticipant: (taskId: string, participantCallId: string, options: { hold: boolean }) => void;
    removeConferenceParticipant: (taskId: string, participantCallId: string) => void;
    resetConferenceState: (taskId: string, conferenceDetails: ConferenceDetails) => void;

    /*
    TODO:// Better way to write it will be `setTaskFromPartialTask`, that create a new task instance base on existing one,
    // maybe using a mix of `Partial<Task>` and type-fest `WritableKeysOf` to limit the edit to keys that are not readonly.
    // See below:
    https://github.com/sindresorhus/type-fest/blob/main/source/writable-keys-of.d.ts
    At the moment and due to time constraints, going with this implementation below.
    */
    setTaskKey: <T extends Task, K extends WritableKeysOf<T>>(taskId: string, key: K, value: T[K]) => void;

    /** Will clear the session history, preserving the current session */
    resetHistory: () => void; // TODO: remove when we remove history
    clearInactiveSession: (sessionId: string) => void;

    /** Will completely reset the store, clearing all sessions, and putting the store in an un-initialized state.
     * This is handy for unit tests, but should not be used in production.
     */
    resetStore: () => void;

    getSession: (sessionId: string) => Session | null;
    getSessionByTaskId: (taskId: string) => Session | null;
    getTask: (taskId: string) => Task | null;
    computed: {
        selectedSession: () => SessionInstance;
        selectedTasks: () => Task[]; // TODO: Temp backwards compat
        sessionIds: () => string[];
        isIdleSession: () => boolean;
    };
}

/** Prevent action exceptions from bubbling up to the rest of the application
 * Without having a try/catch inside every action
 */
function safeAction<T extends unknown[], R>(actionName: string, action: (...args: T) => R): (...args: T) => void {
    return (...args: T): void => {
        try {
            action(...args);
        } catch (error: unknown) {
            logger.warn({ error, actionName }, "Error in SessionStore action");
        }
    };
}

export function createIdleSession() {
    const session = new SessionInstance({
        id: crypto.randomUUID(),
        startedTimestamp: Date.now(),
        kind: "without-customer",
        tasks: new Map(),
        wrappingState: null,
        metadata: {
            startReason: "InitialSession",
            status: "active",
        },
    });
    logger.info({ sessionId: session.id, kind: session.kind }, "Idle session created");
    return session;
}

/**
 * ! The persist middleware calls getItem() during store initialization
 * If these are defined after useSessionStore, accessing DATE_FIELD_KEYS throws a ReferenceError.
 * Note: Keys matched by name only. If these names appear elsewhere and happen to be ISO date strings, they'll be parsed too.
 */
const DATE_FIELD_KEYS = new Set(["assignedAt", "dateCreated", "statusUpdatedAt", "createdOn"]);

/** Reviver function for JSON.parse - converts serialized data back to proper types */
function deserializeReviver(key: string, value: unknown) {
    if ((key === "tasks" || key === "rehydratedTasks") && Array.isArray(value)) {
        return new Map(value);
    }

    if (DATE_FIELD_KEYS.has(key) && typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return value;
}

function serializeReplacer(key: string, value: unknown) {
    if (key.startsWith("_")) return undefined;

    if ((key === "tasks" || key === "rehydratedTasks") && value instanceof Map) {
        return Array.from(value.entries());
    }

    return value;
}

const EMPTY_SESSION_STATE = {
    state: {
        sessions: [],
    },
} as unknown as StorageValue<SessionStore>;

export const useSessionStore = create<SessionStore>()(
    immer(
        persist(
            devtools(
                (set, get) => ({
                    initialized: false,
                    sessions: [],
                    idleSession: createIdleSession(),
                    selectedSessionId: "",
                    mutateStore: safeAction("mutateStore", (mutation: (state: Draft<SessionStore>) => void) =>
                        set(
                            (state) => {
                                mutation(state);
                            },
                            false,
                            "mutateStore",
                        ),
                    ),
                    addTaskToSession: safeAction("addTaskToSession", (sessionId: string, task: Task) =>
                        set(
                            (state) => {
                                if (!sessionId) {
                                    throw new Error(`Cannot add task ${task.id} to undefined session`);
                                }
                                const session = findSessionByIdOrThrow(state, sessionId);
                                session.addTask(task);
                                session.metadata.status = "active";
                            },
                            false,
                            "addTaskToSession",
                        ),
                    ),
                    addSession: safeAction("addSession", (session: SessionInstance, shouldSelect: boolean = false) =>
                        set(
                            (state) => {
                                logger.trace({ session, shouldSelect }, "addSession()");
                                state.sessions.push(session);
                                if (shouldSelect) {
                                    state.selectedSessionId = session.id;
                                }
                            },
                            false,
                            "addSession",
                        ),
                    ),
                    endSession: safeAction("endSession", (sessionId: string, reason: SessionChangeReason) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId, reason }, "endSession()");

                                if (state.selectedSessionId === sessionId) {
                                    state.selectedSessionId = "";
                                }
                                const session = findSessionByIdOrThrow(state, sessionId);
                                session.endSession(reason);
                            },
                            false,
                            "endSession",
                        ),
                    ),
                    setSelectedSession: safeAction("setSelectedSession", (sessionId: string) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId }, "selectSession()");
                                const session = findSessionById(state, sessionId);

                                if (!session && sessionId !== get().idleSession.id) {
                                    throw new Error(`Cannot select session ${sessionId}, not found`);
                                }

                                state.selectedSessionId = sessionId;
                            },
                            false,
                            "selectSession",
                        ),
                    ),
                    clearSelectedSession: safeAction("clearSelectedSession", () =>
                        set(
                            (state) => {
                                logger.trace({ selectedSessionId: state.selectedSessionId }, "clearSelectedSession()");
                                state.selectedSessionId = "";
                            },
                            false,
                            "clearSelectedSession",
                        ),
                    ),
                    updateTaskStatus: safeAction(
                        "updateTaskStatus",
                        (taskId: string, status: TaskStatus, reason?: TaskStatusReason) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, taskStatus: status, reason }, "updateTaskStatus()");
                                    updateTaskStatusInternal(state, taskId, status, reason);
                                },
                                false,
                                "updateTaskStatus",
                            ),
                    ),
                    muteTask: safeAction("muteTask", (taskId: string, shouldMute: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, shouldMute }, "muteTask()");
                                mutateVoiceTask(state, taskId, (task) => {
                                    task.isMuted = shouldMute;
                                });
                            },
                            false,
                            "muteTask",
                        ),
                    ),
                    setHold: safeAction("setHold", (taskId: string, onHold: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, onHold }, "setHold()");
                                mutateVoiceTask(state, taskId, (task) => {
                                    if (onHold && task.holdState.onHold) {
                                        throw new Error("Hold is already true");
                                    }

                                    task.holdState = {
                                        onHold,
                                        onHoldSince: onHold ? Date.now() : null,
                                    };
                                });
                            },
                            false,
                            "setHold",
                        ),
                    ),
                    setStartTimestamp: safeAction("setStartTimestamp", (taskId: string, timestamp: number) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, startTimestamp: timestamp }, "setStartTimestamp()");
                                mutateTrainingTask(state, taskId, (task) => {
                                    task.startTimestamp = timestamp;
                                });
                            },
                            false,
                            "setStartTimestamp",
                        ),
                    ),
                    setWrappingState: safeAction(
                        "setWrappingState",
                        (wrappingState: WrappingState, sessionId: string) =>
                            set(
                                (state) => {
                                    logger.trace({ sessionId, wrappingState }, "setWrappingState()");

                                    const session = findSessionByIdOrThrow(state, sessionId);

                                    if (session.metadata.status !== "active") {
                                        logger.warn(
                                            { sessionId, wrappingState },
                                            "Cannot set WrappingState on an inactive session.",
                                        );
                                        throw new Error("Cannot set WrappingState on an inactive session.");
                                    }

                                    session.wrappingState = wrappingState;
                                },
                                false,
                                "setWrappingState",
                            ),
                    ),
                    clearWrappingState: safeAction("clearWrappingState", (sessionId: string) =>
                        set(
                            (state) => {
                                logger.trace({ sessionId }, "clearWrappingState()");
                                const session = findSessionByIdOrThrow(state, sessionId);
                                session.wrappingState = null;
                            },
                            false,
                            "clearWrappingState",
                        ),
                    ),
                    setConferenceStarted: safeAction("setConferenceStarted", (taskId: string, isStarted: boolean) =>
                        set(
                            (state) => {
                                logger.trace({ taskId, isStarted }, "setConferenceStarted()");
                                mutateVoiceTask(state, taskId, (task) => {
                                    task.conferenceStarted = isStarted;
                                });
                            },
                            false,
                            "setConferenceStarted",
                        ),
                    ),
                    setCustomerLeftTheCall: safeAction("setCustomerLeftTheCall", (taskId: string) =>
                        set(
                            (state) => {
                                logger.trace({ taskId }, "setCustomerLeftTheCall()");
                                mutateVoiceTask(state, taskId, (task) => {
                                    task.holdState = {
                                        onHold: false,
                                        onHoldSince: null,
                                    };
                                    task.customerLeftTheConference = true;
                                });
                            },
                            false,
                            "setCustomerLeftTheCall",
                        ),
                    ),
                    upsertConferenceParticipant: safeAction(
                        "upsertConferenceParticipant",
                        (taskId: string, participant: ConferenceParticipant) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, participant }, "upsertConferenceParticipant()");
                                    mutateVoiceTask(state, taskId, (task) => {
                                        const index = task.conferenceParticipants.findIndex(
                                            (p) => p.participantCallId === participant.participantCallId,
                                        );
                                        if (index === -1) {
                                            task.conferenceParticipants.push(participant);
                                            return;
                                        }
                                        task.conferenceParticipants = task.conferenceParticipants.with(
                                            index,
                                            participant,
                                        );
                                    });
                                },
                                false,
                                "upsertConferenceParticipant",
                            ),
                    ),
                    updateConferenceParticipant: safeAction(
                        "updateConferenceParticipant",
                        (taskId: string, participantCallId: string, options: { hold: boolean }) =>
                            set(
                                (state) => {
                                    logger.trace(
                                        { taskId, participantCallId, options },
                                        "updateConferenceParticipant()",
                                    );
                                    mutateVoiceTask(state, taskId, (task) => {
                                        const participantIndex = task.conferenceParticipants.findIndex(
                                            (p) => p.participantCallId === participantCallId,
                                        );
                                        if (participantIndex === -1) throw new Error("Participant not found");
                                        const participant = task.conferenceParticipants[participantIndex];
                                        task.conferenceParticipants[participantIndex] = {
                                            ...participant,
                                            hold: options.hold ? { since: Date.now() } : undefined,
                                        };
                                    });
                                },
                                false,
                                "updateConferenceParticipant",
                            ),
                    ),
                    removeConferenceParticipant: safeAction(
                        "removeConferenceParticipant",
                        (taskId: string, participantCallId: string) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId, participantCallId }, "removeConferenceParticipant()");
                                    mutateVoiceTask(state, taskId, (task) => {
                                        task.conferenceParticipants = task.conferenceParticipants.filter(
                                            (p) => p.participantCallId !== participantCallId,
                                        );
                                    });
                                },
                                false,
                                "removeConferenceParticipant",
                            ),
                    ),

                    resetConferenceState: safeAction(
                        "resetConferenceState",
                        (taskId: string, conferenceDetails: ConferenceDetails) =>
                            set(
                                (state) => {
                                    logger.trace({ taskId }, "resetConferenceState");
                                    mutateVoiceTask(state, taskId, (task) => {
                                        task.holdState = conferenceDetails.holdState;
                                        task.customerLeftTheConference = conferenceDetails.customerLeftTheConference;
                                        task.conferenceParticipants = conferenceDetails.conferenceParticipants;
                                        task.isMuted = conferenceDetails.isMuted;
                                    });
                                },
                                false,
                                "resetConferenceState",
                            ),
                    ),

                    setTaskKey: <T extends Task, K extends WritableKeysOf<T>>(taskId: string, key: K, value: T[K]) =>
                        set(
                            (state) => {
                                const actionLogger = logger.child({ taskId, taskKey: key });
                                actionLogger.trace("setTaskKey");
                                mutateTask<T>(
                                    state,
                                    taskId,
                                    (task) => {
                                        task[key] = value;
                                    },
                                    actionLogger,
                                );
                            },
                            false,
                            "setTaskKey",
                        ),

                    resetHistory: safeAction("resetHistory", () => {
                        if (import.meta.env.VITE_ENV_TYPE === "production") {
                            throw new Error("resetHistory() should not be called in production");
                        }

                        set(
                            (state) => {
                                state.sessions = [state.sessions[state.sessions.length - 1]];
                            },
                            false,
                            "clearHistory",
                        );
                    }),
                    clearInactiveSession: safeAction("clearInactiveSession", (sessionId: string) =>
                        set(
                            (state) => {
                                state.sessions = state.sessions.filter((s) => s.id !== sessionId);

                                if (state.selectedSessionId && state.selectedSessionId === sessionId) {
                                    state.selectedSessionId = "";
                                }
                            },
                            false,
                            "clearInactiveSession",
                        ),
                    ),
                    resetStore: safeAction("resetStore", () => {
                        if (import.meta.env.VITE_ENV_TYPE === "production") {
                            throw new Error("resetStore() should not be called in production");
                        }

                        set(
                            (state) => {
                                state.sessions = [];
                                state.initialized = false;
                                state.selectedSessionId = "";
                            },
                            false,
                            "resetStore",
                        );
                    }),

                    getTask: (taskId: string) => findTaskById(get(), taskId),
                    getSession: (sessionId: string) => findSessionById(get(), sessionId) as Session | null,
                    getSessionByTaskId: (taskId: string) => {
                        // Reverse search for performance & correctness (most recent sessions first)
                        const sessions = get().sessions;
                        for (let i = sessions.length - 1; i >= 0; i--) {
                            const session = sessions[i];
                            if (session.tasks.has(taskId)) {
                                return session as Session;
                            }
                        }
                        return null;
                    },
                    computed: {
                        selectedSession: () => {
                            const selectedSessionId = get().selectedSessionId;
                            const session = get().sessions.find((s) => s.id === selectedSessionId);
                            return session ?? get().idleSession;
                        },
                        //TODO: Future, actually support multi-sessions with an active session & task
                        selectedTasks: () => {
                            const currentSession = get().computed.selectedSession();
                            return currentSession?.currentTask ? [currentSession.currentTask] : [];
                        },
                        sessionIds: () => get().sessions.map((s) => s.id),
                        isIdleSession: () => ["", get().idleSession.id].includes(get().selectedSessionId),
                    },
                }),
                { enabled: import.meta.env.VITE_ENV_TYPE !== "production", store: "sessions", name: "workspace/sdk" },
            ),
            {
                name: "session-tasks-store",
                // @ts-expect-error types needs to be fixed to allow partial store return value
                partialize: (state) => ({
                    sessions: state.sessions.slice(-50),
                    selectedSessionId: state.selectedSessionId,
                }),
                storage: {
                    getItem(name: string) {
                        const str = localStorage.getItem(name);
                        if (!str) {
                            return EMPTY_SESSION_STATE;
                        }

                        try {
                            const state = JSON.parse(str, deserializeReviver) as SessionStoreState;

                            state.sessions = state.sessions.map((session) => new SessionInstance(session));

                            // To achieve the requirement that we do not expose tasks till post SDK/Twilio init
                            // This is restored upon init
                            state.sessions.forEach((session) => {
                                session.hideRehydratedTasks();
                            });

                            return {
                                state,
                            } as StorageValue<SessionStore>;
                        } catch (error) {
                            logger.warn({ error }, "Error during session hydration from localStorage");
                            return EMPTY_SESSION_STATE;
                        }
                    },
                    setItem(name: string, value: StorageValue<SessionStore>) {
                        const str = safeStringify(value.state, serializeReplacer);
                        localStorage.setItem(name, str ?? "");
                    },
                    removeItem: (name: string) => {
                        localStorage.removeItem(name);
                    },
                },
            },
        ),
    ),
);

function mutateVoiceTask(state: Draft<SessionStore>, taskId: string, mutation: (task: Draft<VoiceTask>) => void) {
    const task = findTaskById(state, taskId);
    if (!task) throw new Error(`${taskId} not found in store`);
    if (!isVoiceTask(task)) throw new Error(`${taskId} isn't a voice task`);

    mutation(task);
}

function mutateTrainingTask(state: Draft<SessionStore>, taskId: string, mutation: (task: Draft<TrainingTask>) => void) {
    const task = findTaskById(state, taskId);
    if (!task) throw new Error(`${taskId} not found in store`);
    if (!isTrainingTask(task)) throw new Error(`${taskId} isn't a training task`);

    mutation(task);
}

function mutateTask<TaskType extends Task>(
    state: Draft<SessionStore>,
    taskId: string,
    mutation: (task: TaskType) => void,
    parentLogger: Logger,
) {
    parentLogger.trace({ taskId }, "mutateTask");
    const task = findTaskById(state, taskId);
    if (!task) {
        logger.warn({ taskId }, "Task not found in store, skipping mutateTask");
        return;
    }
    parentLogger.debug(task.toLog(), "Execute mutateTask");
    mutation(task as TaskType);
}

/** Should not be exposed */
export function updateTaskStatusInternal(
    state: Draft<SessionStore>,
    taskId: string,
    status: TaskStatus,
    reason?: TaskStatusReason,
) {
    const existingTask = findTaskById(state, taskId);
    if (!existingTask) throw new Error(`${taskId} not found in store`);

    // prevent updating timestamps and other derived fields when there is no real status change.
    const prevStatus = existingTask.status;
    const prevReason = existingTask.statusReason;
    if (prevStatus === status && prevReason === reason) {
        logger.trace(
            { taskId, taskStatus: status, reason },
            "updateTaskStatusInternal(): no-op, status and reason unchanged",
        );
        return;
    }

    logger.info(
        { taskId, oldStatus: prevStatus, newStatus: status, oldReason: prevReason, newReason: reason },
        "updateTaskStatusInternal(): applying status update",
    );

    existingTask.status = status;
    existingTask.statusReason = reason;
    existingTask.statusUpdatedAt = new Date();

    //TODO: Why do we do this 👇...?
    if (isVoiceTask(existingTask)) {
        // this section was previously intended to reset voice task state when wrapping
        existingTask.isMuted = false;
        existingTask.holdState = { onHold: false, onHoldSince: null };
    }
}

function findSessionById(state: Draft<SessionStore>, sessionId: string) {
    return state.sessions.find((s) => s.id === sessionId);
}

function findSessionByIdOrThrow(state: Draft<SessionStore>, sessionId: string) {
    const session = findSessionById(state, sessionId);
    if (!session) {
        throw new Error(`Session ${sessionId} not found`);
    }
    return session;
}

/**
 * Resolves a task from the given store state/draft.
 *
 * Inside a `mutateStore`/`set` recipe, ALWAYS resolve tasks you intend to mutate through this
 * helper (passing the immer draft `state`), never through `state.getTask()`. `getTask()` reads
 * `get()` (the committed, immer auto-frozen state), so writing to the object it returns throws
 * `TypeError: Cannot assign to read only property`. Resolving from the draft returns a writable proxy.
 */
export function findTaskById(state: Draft<SessionStore>, taskId: string) {
    const session = state.sessions.find(({ tasks }) => tasks.has(taskId));
    const task = session?.tasks.get(taskId);
    return task ?? null;
}
